import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import {
  getFirestore,
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { getDatabase } from "firebase/database";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: "AIzaSyCi14D1JV3Re83b-mCXER7_cfFUK4DAMks",
  authDomain: "wedding-e6e47.firebaseapp.com",
  databaseURL:
    "https://wedding-e6e47-default-rtdb.asia-southeast1.firebasedatabase.app",
  projectId: "wedding-e6e47",
  storageBucket: "wedding-e6e47.firebasestorage.app",
  messagingSenderId: "43048731320",
  appId: "1:43048731320:web:5aaee55e0a16dc6df42a4d",
  measurementId: "G-4MC88GGHRZ",
};

const app = initializeApp(firebaseConfig);
export const analytics = getAnalytics(app);
export const firestore = getFirestore(app);
export const database = getDatabase(app);
export const storage = getStorage(app);
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();

const FIREBASE_URL =
  "https://wedding-e6e47-default-rtdb.asia-southeast1.firebasedatabase.app/";

const encodeEmail = (email: string) => {
  return email.replace(/[.#$\/[\]]/g, "_");
};

const decodeEmail = (encodedEmail: string) => {
  return encodedEmail.replace(/_/g, ".");
};

// 초기 허용된 이메일 설정
const initializeAllowedEmails = async () => {
  try {
    const response = await fetch(`${FIREBASE_URL}/allowed-emails.json`);
    const data = await response.json();

    if (!data) {
      // 초기 데이터가 없으면 기본 이메일 추가
      await fetch(`${FIREBASE_URL}/allowed-emails.json`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          "j1n6ryu@gmail.com": true,
        }),
      });
    }
  } catch (error) {
    console.error("Error initializing allowed emails:", error);
  }
};

// 앱 시작 시 초기화
initializeAllowedEmails();

export const getAllowedEmails = async () => {
  try {
    const response = await fetch(`${FIREBASE_URL}/allowed-emails.json`);
    const data = await response.json();
    if (!data) return {};

    // 디코딩된 이메일 주소로 변환
    const decodedData: { [key: string]: boolean } = {};
    Object.keys(data).forEach((encodedEmail) => {
      decodedData[decodeEmail(encodedEmail)] = data[encodedEmail];
    });
    return decodedData;
  } catch (error) {
    console.error("Error fetching allowed emails:", error);
    return {};
  }
};

export const addAllowedEmail = async (email: string) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("Authentication required.");
    }
    const token = await user.getIdToken();
    const encodedEmail = encodeEmail(email);

    const response = await fetch(
      `${FIREBASE_URL}/allowed-emails.json?auth=${token}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          [encodedEmail]: true,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to add email: ${JSON.stringify(errorData)}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error adding allowed email:", error);
    throw error;
  }
};

export const removeAllowedEmail = async (email: string) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("Authentication required.");
    }
    const token = await user.getIdToken();
    const encodedEmail = encodeEmail(email);

    const response = await fetch(
      `${FIREBASE_URL}/allowed-emails/${encodedEmail}.json?auth=${token}`,
      {
        method: "DELETE",
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to remove email: ${JSON.stringify(errorData)}`);
    }
  } catch (error) {
    console.error("Error removing allowed email:", error);
    throw error;
  }
};

export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    // 허용된 이메일인지 확인
    const allowedEmails = await getAllowedEmails();
    if (!allowedEmails[user.email || ""]) {
      await signOut(auth);
      throw new Error("접근 권한이 없습니다.");
    }

    return user;
  } catch (error) {
    console.error("Error signing in with Google:", error);
    throw error;
  }
};

export const signOutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Error signing out:", error);
    throw error;
  }
};

export const saveWeddingInfo = async (data: any) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("Authentication required.");
    }
    const token = await user.getIdToken();

    const response = await fetch(
      `${FIREBASE_URL}/wedding/info.json?auth=${token}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Firebase error response:", errorData);
      throw new Error(`Failed to save wedding info: ${errorData.error}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving wedding info:", error);
    throw error;
  }
};

export const getWeddingInfo = async () => {
  try {
    const response = await fetch(`${FIREBASE_URL}/wedding/info.json`);

    if (!response.ok) {
      throw new Error("Failed to fetch wedding info");
    }

    const data = await response.json();

    // 기존 데이터 마이그레이션: father/mother -> fatherName/motherName
    if (data) {
      if (data.groom) {
        if (data.groom.father && !data.groom.fatherName) {
          data.groom.fatherName = data.groom.father;
          delete data.groom.father;
        }
        if (data.groom.mother && !data.groom.motherName) {
          data.groom.motherName = data.groom.mother;
          delete data.groom.mother;
        }
      }
      if (data.bride) {
        if (data.bride.father && !data.bride.fatherName) {
          data.bride.fatherName = data.bride.father;
          delete data.bride.father;
        }
        if (data.bride.mother && !data.bride.motherName) {
          data.bride.motherName = data.bride.mother;
          delete data.bride.mother;
        }
      }
    }

    return data;
  } catch (error) {
    console.error("Error fetching wedding info:", error);
    throw error;
  }
};

// 갤러리 이미지 업로드 (Storage 사용)
export const uploadGalleryImage = async (file: File) => {
  if (!file) throw new Error("파일이 선택되지 않았습니다.");

  const fullFileName = `${Date.now()}_${file.name}`;
  const imageRef = storageRef(storage, `gallery/${fullFileName}`);

  // 파일 업로드
  const snapshot = await uploadBytes(imageRef, file);

  // 다운로드 URL 가져오기
  const url = await getDownloadURL(snapshot.ref);

  // 이미지 크기 가져오기
  const { width, height } = await getImageDimensions(file);

  // Firestore에 메타데이터 저장
  const imageMetaData = {
    url,
    fileName: fullFileName, // Storage에 저장된 전체 파일 이름
    name: file.name, // UI에 표시될 원본 파일 이름
    size: file.size,
    uploadedAt: new Date().toISOString(),
    width,
    height,
  };

  const docRef = await addDoc(collection(firestore, "gallery"), imageMetaData);

  return { id: docRef.id, ...imageMetaData };
};

// 이미지 파일로부터 너비와 높이를 가져오는 헬퍼 함수
const getImageDimensions = (
  file: File
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
};

// 갤러리 이미지 목록 가져오기 (Firestore 사용)
export const getGalleryImages = async () => {
  const galleryCollection = collection(firestore, "gallery");
  const snapshot = await getDocs(galleryCollection);
  const images: { [key: string]: any } = {};
  snapshot.forEach((doc) => {
    images[doc.id] = { id: doc.id, ...doc.data() };
  });
  return images;
};

// 갤러리 이미지 삭제 (Storage 및 Firestore)
export const deleteGalleryImage = async (imageId: string, fileName: string) => {
  if (!imageId || !fileName) {
    throw new Error("삭제할 이미지 정보가 부족합니다.");
  }
  // Firestore에서 문서 삭제
  await deleteDoc(doc(firestore, "gallery", imageId));

  // Storage에서 파일 삭제
  const imageRef = storageRef(storage, `gallery/${fileName}`);
  await deleteObject(imageRef);
};

// 갤러리 이미지 정보 저장
export const saveGalleryImageInfo = async (imageInfo: any) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("Authentication required.");
    }
    const token = await user.getIdToken();

    const response = await fetch(
      `${FIREBASE_URL}/gallery/images/${imageInfo.id}.json?auth=${token}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(imageInfo),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to save image info: ${errorData.error}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving image info:", error);
    throw error;
  }
};

// 갤러리 이미지 순서 변경
export const updateGalleryOrder = async (imageOrder: string[]) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("Authentication required.");
    }
    const token = await user.getIdToken();

    const response = await fetch(
      `${FIREBASE_URL}/gallery/order.json?auth=${token}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(imageOrder),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to update gallery order: ${errorData.error}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating gallery order:", error);
    throw error;
  }
};

// 갤러리 이미지 순서 가져오기
export const getGalleryOrder = async () => {
  try {
    const response = await fetch(`${FIREBASE_URL}/gallery/order.json`);

    if (!response.ok) {
      throw new Error("Failed to fetch gallery order");
    }

    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error("Error fetching gallery order:", error);
    throw error;
  }
};

// 페이지 표시 설정 가져오기
export const getPageVisibility = async () => {
  try {
    const response = await fetch(
      `${FIREBASE_URL}/settings/page-visibility.json`
    );

    if (!response.ok) {
      throw new Error("Failed to fetch page visibility settings");
    }

    const data = await response.json();
    // 기본값 설정
    return {
      hero: data?.hero !== false,
      invitation: data?.invitation !== false,
      gallery: data?.gallery !== false,
      location: data?.location !== false,
      guestbook: data?.guestbook !== false,
      ...data,
    };
  } catch (error) {
    console.error("Error fetching page visibility:", error);
    // 기본값 반환
    return {
      hero: true,
      invitation: true,
      gallery: true,
      location: true,
      guestbook: true,
    };
  }
};

// 페이지 표시 설정 저장
export const savePageVisibility = async (visibility: {
  hero: boolean;
  invitation: boolean;
  gallery: boolean;
  location: boolean;
  guestbook: boolean;
}) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("Authentication required.");
    }
    const token = await user.getIdToken();

    const response = await fetch(
      `${FIREBASE_URL}/settings/page-visibility.json?auth=${token}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(visibility),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to save page visibility: ${errorData.error}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving page visibility:", error);
    throw error;
  }
};
